import React, { Component, createRef } from "react";
import {
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBCardText,
  MDBBtn,
  MDBBtnGroup,
  MDBBadge,
  MDBIcon,
  MDBSwitch,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
} from "mdb-react-ui-kit";
import AdminProjectDetail from "./../AdminProjectDetail/AdminProjectDetail";
import "./AdminProjectsSection.css";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { toast } from "Projects/CC_cubi_clan_project/GlobalComponents/ToastHost/ToastHost";

/* ───── Helpers de reordenamiento decimal ───── */

function computeDecimalOrder(before, after) {
  if (before == null && after == null) return 1;
  if (before == null) return after / 2;
  if (after == null) return before + 1;
  return (before + after) / 2;
}

function needsFullReindex(items) {
  if (items.length < 2) return false;
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  for (let i = 1; i < sorted.length; i++) {
    const diff = sorted[i].sortOrder - sorted[i - 1].sortOrder;
    if (diff < 1e-10) return true;
  }
  return false;
}

function fullReindex(items) {
  const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted.map((it, i) => ({ ...it, sortOrder: i + 1 }));
}

const DEFAULT_PER_PAGE = 10;

export default class AdminProjectsSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      projects: [],
      panelOpen: false,
      selectedId: null,
      showDeleteModal: false,
      projectToDelete: null,
      filter: "all",
      currentPage: 1,
      perPage: DEFAULT_PER_PAGE,
      dragIndex: null,
      overIndex: null,
    };

    this.cardRefs = new Map();
    this.cardsPaneRef = createRef();
  }

  componentDidMount() {
    this.loadProjects();
  }

  /* ───── Carga de datos ───── */

  loadProjects = () => {
    this.props.onLoading && this.props.onLoading(true);
    doFetch("MUGIN", "getProyectosLite", { method: "GET" })
      .then((r) => r.json())
      .then((data) => {
        const projectsRaw = data?.DATA || [];
        const projects = projectsRaw
          .filter((p) => p)
          .sort((x, y) => {
            const sx = Number(x.sortOrder);
            const sy = Number(y.sortOrder);
            if (Number.isFinite(sx) && Number.isFinite(sy)) return sx - sy;
            return 0;
          });
        this.setState({ projects });
      })
      .catch(() => {
        toast.show({
          message: "Ha ocurrido un error al obtener los proyectos",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      })
      .finally(() => {
        this.props.onLoading && this.props.onLoading(false);
      });
  };

  /* ───── Navegación del panel ───── */

  openEditorFor = (slug) => {
    this.setState({ selectedId: slug, panelOpen: true }, () => {
      const el = this.cardRefs.get(slug);
      if (el?.scrollIntoView) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  createNewProject = async () => {
    this.props.onLoading && this.props.onLoading(true);
    try {
      const payload = [{ idProyecto: null, status: "draft" }];
      const response = await doFetch("MUGIN", "upsertProyectos", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data?.DATA?.length > 0) {
        await this.loadProjects();
        this.setState(
          { selectedId: data.DATA[0].slug, panelOpen: true },
          () => {
            this.cardsPaneRef.current?.scrollTo?.({
              top: 0,
              behavior: "smooth",
            });
          }
        );
      }
    } catch (e) {
      toast.show({
        message: "No se pudo crear el proyecto",
        type: "danger",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 5000,
      });
    } finally {
      this.props.onLoading && this.props.onLoading(false);
    }
  };

  closePanel = () => {
    this.setState({ panelOpen: false, selectedId: null });
  };

  onSaveProject = () => {
    this.loadProjects();
  };

  /* ───── Toggle de estado ───── */

  toggleStatus = async (item) => {
    const { idProyecto, status, ...rest } = item;
    const newStatus = status === "published" ? "draft" : "published";

    const updatedItem = { ...item, status: newStatus };
    this.setState((s) => ({
      projects: s.projects.map((p) =>
        p.idProyecto === idProyecto ? updatedItem : p
      ),
    }));

    try {
      const payload = [{ idProyecto, ...rest, status: newStatus }];
      await doFetch("MUGIN", "upsertProyectos", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).then((r) => r.json().catch(() => ({})));
    } catch (e) {
      this.setState((s) => ({
        projects: s.projects.map((p) =>
          p.idProyecto === idProyecto ? { ...p, status } : p
        ),
      }));
      toast.show({
        message: "No se pudo actualizar el estado del proyecto",
        type: "danger",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 5000,
      });
    }
  };

  /* ───── Drag & Drop decimal inteligente ───── */

  getSortedProjects = () =>
    [...this.state.projects].sort((a, b) => a.sortOrder - b.sortOrder);

  onDragStart = (globalIndex) => (e) => {
    this.setState({ dragIndex: globalIndex, overIndex: globalIndex });
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(globalIndex));
    } catch (_) {}
  };

  onDragEnter = (globalIndex) => (e) => {
    e.preventDefault();
    if (globalIndex !== this.state.overIndex) {
      this.setState({ overIndex: globalIndex });
    }
  };

  onDragOver = (globalIndex) => (e) => {
    e.preventDefault();
    if (globalIndex !== this.state.overIndex) {
      this.setState({ overIndex: globalIndex });
    }
  };

  onDrop = (globalIndex) => async (e) => {
    e.preventDefault();
    const { projects } = this.state;
    let fromIdx = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (!Number.isFinite(fromIdx)) fromIdx = this.state.dragIndex;
    const toIdx = globalIndex;

    this.setState({ dragIndex: null, overIndex: null });

    if (fromIdx == null || fromIdx === toIdx) return;

    const sorted = this.getSortedProjects();
    const movedItem = sorted[fromIdx];
    if (!movedItem) return;

    const rest = sorted.filter((_, i) => i !== fromIdx);
    const insertAt = fromIdx < toIdx ? toIdx - 1 : toIdx;

    const before = insertAt > 0 ? rest[insertAt - 1]?.sortOrder : null;
    const after = insertAt < rest.length ? rest[insertAt]?.sortOrder : null;

    const newOrder = computeDecimalOrder(before, after);

    const updatedItem = { ...movedItem, sortOrder: newOrder };
    const updatedProjects = projects.map((p) =>
      p.idProyecto === movedItem.idProyecto ? updatedItem : p
    );

    let itemsToSave;
    if (needsFullReindex(updatedProjects)) {
      const reindexed = fullReindex(updatedProjects);
      this.setState({ projects: reindexed });
      itemsToSave = reindexed.map((p) => ({
        idProyecto: p.idProyecto,
        sortOrder: p.sortOrder,
      }));
    } else {
      this.setState({
        projects: updatedProjects.sort((a, b) => a.sortOrder - b.sortOrder),
      });
      itemsToSave = [
        { idProyecto: movedItem.idProyecto, sortOrder: newOrder },
      ];
    }

    try {
      await doFetch("MUGIN", "reorderProyectos", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(itemsToSave),
      });
    } catch (error) {
      toast.show({
        message: "Error al guardar el nuevo orden",
        type: "danger",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 5000,
      });
      this.loadProjects();
    }
  };

  onDragEnd = () => {
    this.setState({ dragIndex: null, overIndex: null });
  };

  /* ───── Eliminación (con opción de deshabilitar) ───── */

  confirmDelete = (project) => {
    this.setState({ showDeleteModal: true, projectToDelete: project });
  };

  closeDeleteModal = () => {
    this.setState({ showDeleteModal: false, projectToDelete: null });
  };

  handleConfirmDelete = async () => {
    const project = this.state.projectToDelete;
    if (!project) return;

    this.props.onLoading && this.props.onLoading(true);
    try {
      const response = await doFetch(
        "MUGIN",
        "deleteProject?IDProyecto=" + project.idProyecto,
        { method: "DELETE" }
      );
      const data = await response.json();
      if (data && data.STATE === "SUCCESS") {
        await this.loadProjects();
        this.setState({ showDeleteModal: false, projectToDelete: null });
        toast.show({
          message: "Proyecto eliminado correctamente: " + project.title,
          type: "success",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 3000,
        });
      } else {
        toast.show({
          message:
            data?.MESSAGE ||
            "Ha ocurrido un error al borrar el proyecto.",
          type: "danger",
          position: "bottom-center",
          size: "wide",
          animation: "slide",
          timeout: 5000,
        });
      }
    } catch (error) {
      toast.show({
        message: "Ha ocurrido un error no controlado al borrar el proyecto",
        type: "danger",
        position: "bottom-center",
        size: "wide",
        animation: "slide",
        timeout: 5000,
      });
    } finally {
      this.props.onLoading && this.props.onLoading(false);
    }
  };

  disableInsteadOfDelete = async () => {
    const project = this.state.projectToDelete;
    if (!project) return;
    this.setState({ showDeleteModal: false, projectToDelete: null });
    if (project.status === "published") {
      await this.toggleStatus(project);
    }
  };

  /* ───── Filtro y paginación ───── */

  getFilteredProjects = () => {
    const { projects, filter } = this.state;
    const sorted = [...projects].sort((a, b) => a.sortOrder - b.sortOrder);
    if (filter === "published")
      return sorted.filter((p) => p.status === "published");
    if (filter === "draft")
      return sorted.filter((p) => p.status === "draft");
    return sorted;
  };

  /* ───── Render de tarjeta ───── */

  renderCard = (item, globalIdx) => {
    const { panelOpen, selectedId, dragIndex, overIndex } = this.state;
    const isSelected = selectedId === item.slug && panelOpen;

    const showFull = !panelOpen;
    const descClass = showFull
      ? "ap-desc ap-desc--full"
      : "ap-desc ap-desc--clamp1";
    const isDragging = dragIndex === globalIdx;
    const isOver = overIndex === globalIdx;

    return (
      <MDBCard
        key={item.idProyecto}
        ref={(el) => this.cardRefs.set(item.slug, el)}
        className={`admin-projects-card mb-2 ${isSelected ? "selected" : ""} ${isDragging ? "ap-dragging" : ""} ${isOver ? "ap-over" : ""}`}
        title={item.title}
        draggable
        onDragStart={this.onDragStart(globalIdx)}
        onDragEnter={this.onDragEnter(globalIdx)}
        onDragOver={this.onDragOver(globalIdx)}
        onDrop={this.onDrop(globalIdx)}
        onDragEnd={this.onDragEnd}
      >
        <MDBCardBody className="ap-body">
          <div className="ap-head">
            <div className="ap-drag-handle">
              <MDBIcon fas icon="grip-vertical" />
            </div>
            <MDBCardTitle className="ap-title">
              {item.title || "Proyecto sin título"}
            </MDBCardTitle>

            <MDBSwitch
              id={`ap-switch-${item.idProyecto}`}
              className="ap-toggle"
              checked={item.status === "published"}
              onChange={() => this.toggleStatus(item)}
              label=""
              title={item.status === "published" ? "Activo" : "Inactivo"}
            />
          </div>

          {item.description ? (
            <MDBCardText className={descClass}>{item.description}</MDBCardText>
          ) : null}

          <div className="ap-actions">
            <MDBBtn
              size="sm"
              className="mugin-btn-primary"
              onClick={() => this.openEditorFor(item.slug)}
            >
              Editar
            </MDBBtn>
            <MDBBtn
              size="sm"
              color="danger"
              outline
              onClick={() => this.confirmDelete(item)}
            >
              Eliminar
            </MDBBtn>
          </div>
        </MDBCardBody>
      </MDBCard>
    );
  };

  render() {
    const {
      projects,
      panelOpen,
      selectedId,
      filter,
      currentPage,
      perPage,
      showDeleteModal,
      projectToDelete,
    } = this.state;

    const sorted = [...projects].sort((a, b) => a.sortOrder - b.sortOrder);
    const filtered = this.getFilteredProjects();
    const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pageItems = filtered.slice(
      (safeCurrentPage - 1) * perPage,
      safeCurrentPage * perPage
    );

    const totalCount = projects.length;
    const publishedCount = projects.filter(
      (p) => p.status === "published"
    ).length;
    const draftCount = totalCount - publishedCount;

    return (
      <section
        className={`admin-projects-layout ${panelOpen ? "is-open" : ""}`}
      >
        {/* Pane izquierdo */}
        <div className="cards-pane" ref={this.cardsPaneRef}>
          <div className="cards-toolbar">
            <MDBBtnGroup size="sm">
              <MDBBtn
                color={filter === "all" ? "primary" : "light"}
                onClick={() =>
                  this.setState({ filter: "all", currentPage: 1 })
                }
              >
                Todos
                <MDBBadge className="ms-1" color="dark" pill>
                  {totalCount}
                </MDBBadge>
              </MDBBtn>
              <MDBBtn
                color={filter === "published" ? "success" : "light"}
                onClick={() =>
                  this.setState({ filter: "published", currentPage: 1 })
                }
              >
                <MDBIcon fas icon="eye" className="me-1" />
                Activos
                <MDBBadge className="ms-1" color="dark" pill>
                  {publishedCount}
                </MDBBadge>
              </MDBBtn>
              <MDBBtn
                color={filter === "draft" ? "warning" : "light"}
                onClick={() =>
                  this.setState({ filter: "draft", currentPage: 1 })
                }
              >
                <MDBIcon fas icon="eye-slash" className="me-1" />
                Borrador
                <MDBBadge className="ms-1" color="dark" pill>
                  {draftCount}
                </MDBBadge>
              </MDBBtn>
            </MDBBtnGroup>

            <MDBBtn
              color="success"
              size="sm"
              className="ms-auto"
              onClick={this.createNewProject}
            >
              Nuevo proyecto
            </MDBBtn>
          </div>

          {/* Lista de tarjetas con drag & drop */}
          <div className="ap-list">
            {pageItems.map((item) => {
              const globalIdx = sorted.findIndex(
                (p) => p.idProyecto === item.idProyecto
              );
              return this.renderCard(item, globalIdx);
            })}
          </div>

          {/* Paginador */}
          {totalPages > 1 && (
            <nav className="d-flex justify-content-center mt-3">
              <ul className="pagination pagination-sm">
                <li
                  className={`page-item ${safeCurrentPage <= 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      this.setState({ currentPage: safeCurrentPage - 1 })
                    }
                  >
                    &laquo;
                  </button>
                </li>
                {Array.from({ length: totalPages }, (_, i) => (
                  <li
                    key={i}
                    className={`page-item ${i + 1 === safeCurrentPage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => this.setState({ currentPage: i + 1 })}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
                <li
                  className={`page-item ${safeCurrentPage >= totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      this.setState({ currentPage: safeCurrentPage + 1 })
                    }
                  >
                    &raquo;
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>

        {/* Pane derecho: detalle con animación */}
        <div className="detail-pane">
          {panelOpen && (
            <div className="detail-card">
              <AdminProjectDetail
                projectId={selectedId}
                onClose={() => this.closePanel()}
                onSave={() => this.onSaveProject()}
              />
            </div>
          )}
        </div>

        {/* Modal de eliminación (con opción de deshabilitar) */}
        <MDBModal
          show={showDeleteModal}
          onHide={this.closeDeleteModal}
          tabIndex="-1"
        >
          <MDBModalDialog centered>
            <MDBModalContent>
              <MDBModalHeader>
                <MDBModalTitle>
                  <MDBIcon
                    fas
                    icon="exclamation-triangle"
                    className="text-warning me-2"
                  />
                  Confirmar eliminación
                </MDBModalTitle>
                <MDBBtn
                  className="btn-close"
                  color="none"
                  onClick={() =>
                    this.setState({
                      showDeleteModal: false,
                      projectToDelete: null,
                    })
                  }
                />
              </MDBModalHeader>
              <MDBModalBody>
                <p>
                  ¿Está seguro de que desea eliminar el proyecto{" "}
                  <strong>"{projectToDelete?.title}"</strong>?
                </p>
                <p className="text-muted small">
                  Esta acción no se puede deshacer. Si solo desea ocultarlo
                  temporalmente, puede deshabilitarlo en su lugar.
                </p>
              </MDBModalBody>
              <MDBModalFooter className="d-flex justify-content-between">
                <MDBBtn
                  color="secondary"
                  outline
                  onClick={() =>
                    this.setState({
                      showDeleteModal: false,
                      projectToDelete: null,
                    })
                  }
                >
                  Cancelar
                </MDBBtn>
                <div>
                  {projectToDelete?.status === "published" && (
                    <MDBBtn
                      color="warning"
                      className="me-2"
                      onClick={() => this.disableInsteadOfDelete()}
                    >
                      <MDBIcon fas icon="eye-slash" className="me-1" />
                      Deshabilitar
                    </MDBBtn>
                  )}
                  <MDBBtn
                    color="danger"
                    onClick={() => this.handleConfirmDelete()}
                  >
                    <MDBIcon fas icon="trash" className="me-1" />
                    Eliminar
                  </MDBBtn>
                </div>
              </MDBModalFooter>
            </MDBModalContent>
          </MDBModalDialog>
        </MDBModal>
      </section>
    );
  }
}
